/**
 * Manager Review Flags Configuration
 *
 * Defines conditions that require manager attention during model planner review.
 * Flags are computed dynamically from existing answers - not stored separately.
 */

/**
 * Get a display name for an item based on section type
 */
const getItemName = (section, answers) => {
  if (!answers) return `Item`;

  switch (section) {
    case 'ponds':
      return answers.pond_name || 'Unnamed Pond';
    case 'curb':
      return answers.curb_type_name || 'Unnamed Curb';
    case 'walls':
      return answers.wall_name_location || 'Unnamed Wall';
    default:
      return 'Item';
  }
};

/**
 * Flag definitions with detection logic
 *
 * Each flag has:
 * - section: Which planner section this applies to
 * - severity: 'critical' | 'warning' | 'info'
 * - message: Description shown to manager
 * - decisionType: 'acknowledge' | 'choice'
 * - decisionOptions: For 'choice' type, array of options
 * - detect: Function that returns true if flag should be raised
 */
export const FLAG_DEFINITIONS = {
  // ========== POND FLAGS ==========

  pond_only_lowest_contour: {
    section: 'ponds',
    severity: 'warning',
    message: 'Only lowest contour provided for pond bottom - no table or profile data available',
    decisionType: 'acknowledge',
    detect: (answers) => {
      // Flag if grading plan has info but no table/profile bottom elevation
      return answers.has_grading_plan_info === true &&
             answers.grading_plan_lowest_contour &&
             !answers.table_bottom_elevation &&
             !answers.profile_bottom_elevation &&
             !answers.detail_bottom_elevation &&
             !answers.plan_view_bottom_elevation;
    }
  },

  pond_missing_spillway: {
    section: 'ponds',
    severity: 'warning',
    message: 'No spillway elevation found from any source',
    decisionType: 'acknowledge',
    detect: (answers) => {
      // Check if we have any pond data but no spillway
      const hasPondData = answers.has_pond_profile === true ||
                          answers.has_pond_table === true ||
                          answers.has_pond_detail === true ||
                          answers.has_grading_plan_info === true;

      const hasSpillway = answers.profile_spillway_elevation ||
                          answers.table_spillway_elevation ||
                          answers.detail_spillway_elevation ||
                          answers.plan_view_spillway_elevation ||
                          answers.grading_plan_spillway_elevation;

      return hasPondData && !hasSpillway;
    }
  },

  // ========== CURB FLAGS (with gutter) ==========

  curb_elevation_surface: {
    section: 'curb',
    severity: 'critical',
    message: 'Curb has gutter - decide what surface to use for elevation',
    decisionType: 'choice',
    decisionOptions: [
      'Elevate at Flowline (FL)',
      'Elevate at Top of Curb (TC)',
      'Elevate at Edge of Pavement (EP)'
    ],
    detect: (answers) => answers.has_gutter === true
  },

  curb_model_gutter: {
    section: 'curb',
    severity: 'critical',
    message: 'Decide how to model the gutter',
    decisionType: 'choice',
    decisionOptions: [
      'Model full gutter profile',
      'Model to FL only (skip gutter)'
    ],
    detect: (answers) => answers.has_gutter === true
  },

  curb_linework_mismatch: {
    section: 'curb',
    severity: 'warning',
    message: 'Linework width does not match curb detail width',
    decisionType: 'acknowledge',
    detect: (answers) => answers.linework_matches_detail === false
  }
};

/**
 * Compute all flags based on current section items
 *
 * @param {Object} sectionItems - Object with section keys containing arrays of items
 * @param {Object} managerDecisions - Existing manager decisions (to check what's already decided)
 * @returns {Array} Array of flag objects
 */
export function computeFlags(sectionItems, managerDecisions = {}) {
  const flags = [];

  for (const [flagId, flagDef] of Object.entries(FLAG_DEFINITIONS)) {
    const sectionKey = flagDef.section;
    const items = sectionItems[sectionKey] || [];

    items.forEach((item, index) => {
      const itemAnswers = item.answers || {};

      // Check if this flag's condition is met
      if (flagDef.detect(itemAnswers)) {
        const decisionField = `manager_review.${sectionKey}.${index}.${flagId}`;

        flags.push({
          id: flagId,
          section: sectionKey,
          itemIndex: index,
          itemName: getItemName(sectionKey, itemAnswers),
          severity: flagDef.severity,
          message: flagDef.message,
          decisionType: flagDef.decisionType,
          decisionOptions: flagDef.decisionOptions || null,
          decisionField: decisionField,
          currentDecision: managerDecisions[decisionField] || null
        });
      }
    });
  }

  // Sort by severity (critical first, then warning, then info)
  const severityOrder = { critical: 0, warning: 1, info: 2 };
  flags.sort((a, b) => {
    const aSev = severityOrder[a.severity] ?? 3;
    const bSev = severityOrder[b.severity] ?? 3;
    if (aSev !== bSev) return aSev - bSev;
    // Within same severity, sort by section then item index
    if (a.section !== b.section) return a.section.localeCompare(b.section);
    return a.itemIndex - b.itemIndex;
  });

  return flags;
}

/**
 * Check if all critical flags have decisions
 */
export function allCriticalDecisionsMade(flags, decisions) {
  const criticalFlags = flags.filter(f => f.severity === 'critical');
  return criticalFlags.every(f => decisions[f.decisionField]);
}

/**
 * Get summary of flags by severity
 */
export function getFlagSummary(flags) {
  return {
    critical: flags.filter(f => f.severity === 'critical').length,
    warning: flags.filter(f => f.severity === 'warning').length,
    info: flags.filter(f => f.severity === 'info').length,
    total: flags.length
  };
}
